import { useSelector } from 'react-redux';
import { useActor } from '@xstate/react';
import { map } from 'lodash';
import {
  AdsSpend,
  ClientsCharge,
  EmployeeCount,
  Industry,
  JobRole,
  LeadsList,
  OnboardingGoal,
  WebsiteTraffic,
} from '@mm/state/model/CompanyModel';
import { selectCurrentUser } from '@mm/state/selectors/userSelector';
import { SignUpFlowEventsTypes } from '../../../../../../signUpFlowMachine/signUpMachineEventTypes';
import { SignUpFlowMachineService } from '../../../../../../signUpFlowMachine/signUpMachineTypes';
import {
  AdBudgetIcons,
  ClientChargeIcons,
  EmployeesCountIcons,
  GoalIcons,
  IndustryIcons,
  JobRoleIcons,
  OnboardingIcons,
} from './OnboardingQuestionIcons/OnboardingQuestionIcons';
import { companyFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';

export type CompanyInfoSteps = {
  question?: string;
  buttons?: {
    text: string;
    action: () => void;
    isSelected?: boolean;
  }[];
  icons?: OnboardingIcons[];
  videoButton?: {
    text: string;
    action: () => void;
  };
  subtitle?: string;
  subtitleHighlighted?: string;
  inputPlaceholder?: string;
  onNext?: (text: string) => void;
  video?: string;
  videoDescription?: {
    description?: string;
    descriptionLineTwo?: string;
    descriptionHeader?: string;
    descriptionImage?: string;
  };
  showSignature?: boolean;
};

const teamSizeButtonsList: {
  text: EmployeeCount;
}[] = [
  {
    text: EmployeeCount.optionOne,
  },
  {
    text: EmployeeCount.optionTwo,
  },
  {
    text: EmployeeCount.optionThree,
  },
  {
    text: EmployeeCount.optionFour,
  },
];

export const useCompanyInfoStepBuilder = (
  service: SignUpFlowMachineService,
) => {
  const [current, send] = useActor(service);
  const { company, stepOptions } = current.context;
  const user = useSelector(selectCurrentUser);
  const isNewSignupFlow = useSelector(
    companyFeatureFlagsSelector,
  ).onboarding2022;
  const teamSizeButtonsText = map(teamSizeButtonsList, 'text');

  const steps: CompanyInfoSteps[] = [
    {
      question: 'What is your job role?',
      buttons: [
        JobRole.creator,
        JobRole.marketing,
        JobRole.sales,
        JobRole.ownBusiness,
        JobRole.agency,
      ].map(role => {
        return {
          text: role,
          action: () =>
            send({
              type: SignUpFlowEventsTypes.SELECT_JOB_ROLE,
              data: {
                jobRole: role,
              },
            }),
          isSelected: role === company?.jobRole,
        };
      }),
      icons: [
        JobRoleIcons.creator,
        JobRoleIcons.marketing,
        JobRoleIcons.sales,
        JobRoleIcons.ownBusiness,
        JobRoleIcons.agency,
      ],
    },
    {
      question: 'What is your industry?',
      buttons: [
        Industry.eCommerce,
        Industry.travel,
        Industry.software,
        Industry.nonprofit,
        Industry.education,
        Industry.realEstate,
        Industry.legal,
        Industry.recruitment,
        Industry.financial,
        Industry.insurance,
        Industry.rehab,
        Industry.advertising,
        Industry.other,
      ].map(industry => {
        return {
          text: industry,
          action: () => {
            send({
              type: SignUpFlowEventsTypes.SELECT_INDUSTRY,
              data: {
                industry,
              },
              isOtherIndustrySelected: false,
            });
          },
          isSelected:
            (industry === Industry.other &&
              stepOptions.isOtherIndustrySelected) ||
            industry === company?.industry,
        };
      }),
      icons: [
        IndustryIcons.eCommerce,
        IndustryIcons.travel,
        IndustryIcons.software,
        IndustryIcons.nonprofit,
        IndustryIcons.education,
        IndustryIcons.realEstate,
        IndustryIcons.legal,
        IndustryIcons.recruitment,
        IndustryIcons.financial,
        IndustryIcons.insurance,
        IndustryIcons.rehab,
        IndustryIcons.advertising,
        IndustryIcons.other,
      ],
      inputPlaceholder: 'Please fill your industry',
      onNext: text => {
        send({
          type: SignUpFlowEventsTypes.SELECT_INDUSTRY,
          data: {
            industry: text,
          },
          isOtherIndustrySelected: true,
        });
      },
    },
    {
      question: 'What is your company size?',
      buttons: teamSizeButtonsText.map(employees => {
        return {
          text: employees,
          action: () =>
            send({
              type: SignUpFlowEventsTypes.SELECT_TEAM_SIZE,
              data: {
                employeeCount: employees,
              },
            }),
          isSelected: employees === company?.employeeCount,
        };
      }),
      icons: [
        EmployeesCountIcons.lessThanFive,
        EmployeesCountIcons.lessThanTwenty,
        EmployeesCountIcons.lessThanFifty,
        EmployeesCountIcons.lessThanTwoHundred,
      ],
    },
    {
      question: 'What is your goal with MobileMonkey?',
      buttons: isNewSignupFlow
        ? []
        : [
            OnboardingGoal.appointmentBooking,
            OnboardingGoal.automateSocialPlatformSales,
            OnboardingGoal.marketingFunnelOptimization,
            OnboardingGoal.automateLeadQualification,
            OnboardingGoal.findInfluencers,
            OnboardingGoal.outboundLeadGen,
          ].map(goal => {
            return {
              text: goal,
              action: () =>
                send({
                  type: SignUpFlowEventsTypes.SELECT_GOAL,
                  data: {
                    goal,
                  },
                }),
              isSelected: goal === company?.goal,
            };
          }),
      icons: [
        GoalIcons.appointmentBooking,
        GoalIcons.automateSocialPlatformSales,
        GoalIcons.marketingFunnelOptimization,
        GoalIcons.automateLeadQualification,
        GoalIcons.findInfluencers,
        GoalIcons.outboundLeadGen,
      ],
      inputPlaceholder: 'Please, fill your goal',
      onNext: text => {
        send({
          type: SignUpFlowEventsTypes.SELECT_GOAL,
          data: {
            goal: text,
          },
        });
      },
    },
    {
      question: 'What is your monthly advertising spend?',
      buttons: [
        AdsSpend.optionOne,
        AdsSpend.optionTwo,
        AdsSpend.optionThree,
      ].map(budget => {
        return {
          text: budget,
          action: () =>
            send({
              type: SignUpFlowEventsTypes.SELECT_MONTHLY_AD_BUDGET,
              data: {
                monthlyAdBudget: budget,
              },
            }),
          isSelected: budget === company?.monthlyAdBudget,
        };
      }),
      icons: [
        AdBudgetIcons.noAdBudget,
        AdBudgetIcons.lessThenFifteenHundred,
        AdBudgetIcons.lessThan10K,
      ],
    },
    {
      question: 'What is your average product price or annual deal size?',
      buttons: [
        ClientsCharge.optionOne,
        ClientsCharge.optionTwo,
        ClientsCharge.optionThree,
      ].map(charge => {
        return {
          text: charge,
          action: () =>
            send({
              type: SignUpFlowEventsTypes.SELECT_CLIENTS_CHARGE,
              data: {
                clientsCharge: charge,
              },
            }),
          isSelected: charge === company?.clientsCharge,
        };
      }),
      icons: [
        ClientChargeIcons.lessThenHundred,
        ClientChargeIcons.lessThenTwoFifty,
        ClientChargeIcons.lessThenFiveHundred,
      ],
    },
    {
      question: 'What is your monthly website traffic?',
      buttons: [
        WebsiteTraffic.optionOne,
        WebsiteTraffic.optionTwo,
        WebsiteTraffic.optionThree,
      ].map(visitors => {
        return {
          text: visitors,
          action: () =>
            send({
              type: SignUpFlowEventsTypes.SELECT_MONTHLY_WEBSITE_TRAFFIC,
              data: {
                websiteVisitors: visitors,
              },
            }),
          isSelected: visitors === company?.websiteVisitors,
        };
      }),
      icons: [
        EmployeesCountIcons.lessThanFifty,
        EmployeesCountIcons.lessThanTwoHundred,
        EmployeesCountIcons.moreThanTwoHundred,
      ],
    },
    {
      question:
        'What is the size of your existing lead list (emails or phone numbers)?',
      buttons: [
        LeadsList.optionOne,
        LeadsList.optionTwo,
        LeadsList.optionThree,
      ].map(leads => {
        return {
          text: leads,
          action: () =>
            send({
              type: SignUpFlowEventsTypes.SELECT_LEAD_LIST_SIZE,
              data: {
                leadsList: leads,
              },
            }),
          isSelected: leads === company?.leadsList,
        };
      }),
      icons: [
        EmployeesCountIcons.lessThanFifty,
        EmployeesCountIcons.lessThanTwoHundred,
        EmployeesCountIcons.moreThanTwoHundred,
      ],
    },
    {
      question: `Hey ${user?.firstName}, This is Larry Kim, CEO of MobileMonkey`,
      videoButton: {
        text: 'Let’s find some Unicorns!',
        action: () =>
          send({
            type: SignUpFlowEventsTypes.WATCH_VIDEO,
          }),
      },
      video:
        'https://player.vimeo.com/video/688027650?h=a469fc9623&autoplay=1&loop=1&autopause=0&muted=1',
      videoDescription: {
        description: `I love qualified leads (Unicorns!), but I hate unqualified leads (Donkeys!).`,
        descriptionLineTwo: `So I designed MobileMonkey to help automate the process of finding your unicorns in a sea of donkeys.`,
      },
    },
  ];

  return { steps };
};
